import React, { FC, ReactNode, useCallback, useState } from 'react';

import computeScrollIntoView from 'compute-scroll-into-view';

import Button from '@adsk/alloy-react-button';
import {
  DropdownMenu,
  useDropdown,
  UseDropdownProps,
  PLACEMENTS,
} from '@adsk/alloy-react-dropdown';
import { ChevronDownIcon } from '@adsk/alloy-react-icon';
import theme, { StyleProp } from '@adsk/alloy-react-theme';

type Item = {
  key: number;
  label: ReactNode;
};

type YearMonthDropdownProps = Pick<
  UseDropdownProps<Item>,
  'items' | 'selectedItem' | 'onSelectedItemChange'
> & {
  children: ReactNode;
  items: Item[];
  style?: StyleProp;
  disabled?: boolean;
  'data-testid'?: string;
};

const YearMonthDropdown: FC<YearMonthDropdownProps> = ({
  style,
  children,
  disabled,
  'data-testid': dataTestId,
  ...useDropdownProps
}) => {
  const [isInitialScroll, setIsInitialScroll] = useState(true);

  /**
   * This is the default downshift behaviour, modified on initial scroll
   * https://github.com/downshift-js/downshift/blob/bcc793a194a7c30f84b4514b8541a6b7ee3d991f/src/utils.js#L25-L39
   */
  const centerOnInitialScroll = useCallback(
    (node: HTMLElement, menuNode: HTMLElement) => {
      const actions = computeScrollIntoView(node, {
        boundary: menuNode,
        block: isInitialScroll ? 'center' : 'nearest',
        scrollMode: 'if-needed',
      });
      actions.forEach(({ el, top, left }) => {
        el.scrollTop = top;
        el.scrollLeft = left;
      });

      setIsInitialScroll(false);
    },
    [isInitialScroll],
  );

  const { controlProps, menuProps } = useDropdown<Item, HTMLDivElement>({
    placement: PLACEMENTS.BOTTOM_START,
    scrollIntoView: centerOnInitialScroll,
    ...useDropdownProps,
  });

  const { ref, isOpen, getToggleButtonProps } = controlProps;

  return (
    <div ref={ref}>
      <Button
        data-testid={dataTestId}
        style={[
          {
            padding: `0 ${theme.spacing.XS}px`,
            border: 'none',
            width: 'auto',
            color: theme.tokens.colors.text.primary.value,
            '&:disabled': {
              color: theme.tokens.colors.text.inactive.value,
            },
          },
          style,
        ]}
        variant={Button.VARIANTS.TERTIARY}
        {...getToggleButtonProps()}
        selected={isOpen}
        disabled={disabled}
      >
        {children}
        <ChevronDownIcon
          style={{
            marginLeft: theme.spacing.XS,
            width: 18,
            height: 18,
          }}
        />
      </Button>
      <DropdownMenu
        style={{
          width: 130,
          maxHeight: 272,
          overflowY: 'auto',
        }}
        renderFooter={() => null} // Cannot clear this dropdown, so no footer needed
        {...menuProps}
      />
    </div>
  );
};

YearMonthDropdown.displayName = 'YearMonthDropdown';

export default YearMonthDropdown;
